import { useCallback, useMemo } from 'react';

import { useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import { useMutation } from '@zenbusiness/application-commons-graphql';
import Box from '@zenbusiness/zui/Box';
import Divider from '@zenbusiness/zui/Divider';
import Heading from '@zenbusiness/zui/Heading';
import LoadingButton from '@zenbusiness/zui/LoadingButton';
import Paper from '@zenbusiness/zui/Paper';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import { useSearchParams } from 'react-router-dom';

import { FocusGrid, ContainerGrid, ActionButtonsGrid } from '#client/components/Grid';
import { ACTION_BUTTON_TYPE } from '#client/components/Grid/constants';
import RecipientDetail from '#client/components/MoveMoney/components/SummaryFormComponents/RecipientDetail';
import SenderDetail from '#client/components/MoveMoney/components/SummaryFormComponents/SenderDetail';
import TransactionDetail from '#client/components/MoveMoney/components/SummaryFormComponents/TransactionDetail';
import {
  convertCentToUSDText,
  isChaseBank,
  resetCounterpartyOnUrl
} from '#client/components/MoveMoney/utils/common';
import { COUNTERPARTY_CONNECTED_TYPE } from '#client/constants/counterParty';
import { CURRENCY } from '#client/constants/currencies';
import { DATE_FORMAT } from '#client/constants/date';
import { COMMON_ERROR_MESSAGE_DESC } from '#client/constants/messages';
import { COUNTERPARTY_ID } from '#client/constants/modals';
import { BANKING_PAYMENT_TYPE } from '#client/constants/payment';
import { TOKEN_TYPES } from '#client/constants/tokens';
import {
  CONFIRMATION_FORM_STEP,
  HOW_MUCH_STEP,
  MAP_UNIT_STATUS_TO_ZENBUSINESS_RESPONSE,
  MOVE_MONEY_AGREEMENT,
  TIME_LINE,
  TRANSFER_TYPE,
  WHICH_ACCOUNT_STEP
} from '#client/constants/transfer';
import useConstBusinessEntityUuid from '#client/hooks/useConstBusinessEntityUuid';
import useCustomerToken from '#client/hooks/useCustomerToken';
import useEnterSubmit from '#client/hooks/useEnterSubmit';
import useFormStep from '#client/hooks/useFormStep';
import useModalDisplayTracker from '#client/hooks/useModalDisplayTracker';
import useToastContext from '#client/hooks/useToastContext';
import { TRANSFER_MUTATION, WITHDRAW_MUTATION } from '#client/mutations';

import { DialogContentStyled, Wrapper } from './IsThisCorrectForm.styles';

export default function IsThisCorrectFormACH({ handleClose, handleClosePreviousModal }) {
  const { businessEntityUuid } = useConstBusinessEntityUuid();

  const [searchParams, setSearchParams] = useSearchParams();
  const counterpartyId = searchParams.get(COUNTERPARTY_ID);

  const { data, setCurrentFormStep, setData } = useFormStep();

  const { selectedToAccount, amount, transferType, fromAccount, description, idempotencyKey } =
    data;
  const toName = selectedToAccount.name ?? '';
  const toBank = selectedToAccount.bank ?? '';
  const isHideEyeBtn = isChaseBank({
    bank: toBank,
    connectedType: selectedToAccount?.connectedType
  });
  const date = useMemo(() => dayjs(new Date()).format(DATE_FORMAT.MOVE_MONEY), []);
  const amountInUSD = convertCentToUSDText(amount);
  const fromName = fromAccount.name ?? '';
  const timeline =
    transferType === TRANSFER_TYPE.STANDARD_ACH.value
      ? TIME_LINE.STANDARD_ACH
      : TIME_LINE.SAME_DAY_ACH;

  useModalDisplayTracker({
    elementName: 'authorize_transaction_sm_modal',
    elementText: 'authorize_transaction_sm_modal',
    appPageName: 'active_banking_dashboard'
  });

  const handleEditTransactionDetails = () => {
    setCurrentFormStep(HOW_MUCH_STEP);
    setData({
      ...data,
      isDisableBack: false
    });
    if (counterpartyId) {
      resetCounterpartyOnUrl(setSearchParams, COUNTERPARTY_ID);
    }
  };

  const handleEditRecipient = () => {
    setCurrentFormStep(WHICH_ACCOUNT_STEP);
    setData({
      ...data,
      isDisableBack: false,
      transferType: TRANSFER_TYPE.STANDARD_ACH.value
    });
    if (counterpartyId) {
      resetCounterpartyOnUrl(setSearchParams, COUNTERPARTY_ID);
    }
  };

  const [transfer, { loading: loadingTransfer }] = useMutation(TRANSFER_MUTATION, {
    noThrow: true
  });

  const [withdraw, { loading: loadingWithdraw }] = useMutation(WITHDRAW_MUTATION, {
    noThrow: true
  });

  const { getCustomerToken, loading: loadingToken } = useCustomerToken({
    type: TOKEN_TYPES.COMMON
  });
  const authorizeButtonId = useEnterSubmit();
  const { createErrorToast } = useToastContext();

  const loadingSubmit = loadingToken || loadingTransfer || loadingWithdraw;

  const handleResponse = useCallback(
    (response) => {
      const reason = response?.data?.createBankingPayment?.reason;
      const error = response?.error;
      const errorMessage = MAP_UNIT_STATUS_TO_ZENBUSINESS_RESPONSE[reason];

      if (errorMessage || error) {
        createErrorToast({
          title: 'Payment failed',
          description: errorMessage || error?.message || COMMON_ERROR_MESSAGE_DESC,
          error
        });
        handleClose();
        handleClosePreviousModal();
      } else {
        const dateInit = response?.data?.createBankingPayment?.createdAt;
        const transactionId = response?.data?.createBankingPayment?.transactionId;

        // Prepare data for Confirmation Step
        setData({
          ...data,
          confirmationTitle: 'Your transfer is scheduled',
          dateInit,
          transactionId
        });
        setCurrentFormStep(CONFIRMATION_FORM_STEP);
      }
    },
    [createErrorToast, data, handleClose, handleClosePreviousModal, setCurrentFormStep, setData]
  );

  const handleSubmitTransfer = async () => {
    const customerToken = await getCustomerToken();

    if (!customerToken) {
      handleClose();
      return;
    }

    const transferNote = description || undefined;
    const response = await transfer({
      variables: {
        input: {
          businessEntityUuid,
          amount: {
            currency: CURRENCY.USD,
            amount
          },
          counterpartyId: data?.selectedToAccount?.id,
          sameDay: transferType === TRANSFER_TYPE.SAME_DAY_ACH.value,
          description: transferNote,
          idempotencyKey,
          customerToken,
          type: BANKING_PAYMENT_TYPE.TRANSFER
        }
      }
    });
    handleResponse(response);
  };

  const handleSubmitWithdraw = async () => {
    const customerToken = await getCustomerToken();

    if (!customerToken) {
      handleClose();
      return;
    }

    const withdrawNote = description || undefined;
    const response = await withdraw({
      variables: {
        input: {
          businessEntityUuid,
          amount: {
            currency: CURRENCY.USD,
            amount
          },
          counterpartyId: data?.selectedToAccount?.id,
          sameDay: transferType === TRANSFER_TYPE.SAME_DAY_ACH.value,
          description: withdrawNote,
          idempotencyKey,
          customerToken,
          type: BANKING_PAYMENT_TYPE.WITHDRAW
        }
      }
    });
    handleResponse(response);
  };

  const trackAuthorizeClick = useButtonPressedCallback({
    elementName: 'authorize_transfer',
    elementText: 'Authorize',
    appPageName: 'authorize_transaction_sm_modal'
  });

  const handleSubmit = () => {
    trackAuthorizeClick();
    if (data?.selectedToAccount?.connectedType === COUNTERPARTY_CONNECTED_TYPE.PLAID) {
      handleSubmitWithdraw();
    } else {
      handleSubmitTransfer();
    }
  };

  return (
    <>
      <DialogContentStyled>
        <ContainerGrid>
          <FocusGrid>
            <Wrapper role="form">
              <Stack
                alignItems="center"
                paddingBottom={{ xs: '32px', lg: '40px' }}
              >
                <Heading
                  family="secondary"
                  size="3xl"
                  weight="bold"
                >
                  Is this correct?
                </Heading>
              </Stack>
              <Paper variant="outlined">
                <TransactionDetail
                  amount={amountInUSD}
                  dateInitiated={date}
                  handleEdit={handleEditTransactionDetails}
                  hasEdit
                  timeline={timeline}
                  transactionDescription={description}
                  transferType={transferType}
                />
                <Divider />
                <RecipientDetail
                  accountHolder={toName}
                  accountNumber={selectedToAccount.accountNumber}
                  bank={toBank}
                  fullAccountNumber={isHideEyeBtn ? '' : selectedToAccount.fullAccountNumber}
                  handleEdit={handleEditRecipient}
                  hasEdit
                />
                <Divider />
                <SenderDetail
                  accountHolder={fromName}
                  accountNumber={fromAccount.accountNumber}
                  bank="ZenBusiness"
                  fullAccountNumber={fromAccount.accountNumber}
                />
              </Paper>
              <Box marginTop={{ xs: '32px', lg: '40px' }}>
                <Text
                  align="left"
                  color="tertiary"
                  size="sm"
                >
                  {MOVE_MONEY_AGREEMENT.LINE_1}
                </Text>
                <br />
                <Text
                  align="left"
                  color="tertiary"
                  size="sm"
                >
                  {MOVE_MONEY_AGREEMENT.LINE_2}
                </Text>
                <br />
                <Text
                  align="left"
                  color="tertiary"
                  size="sm"
                >
                  {MOVE_MONEY_AGREEMENT.LINE_3}
                </Text>
              </Box>
            </Wrapper>
          </FocusGrid>
        </ContainerGrid>
      </DialogContentStyled>
      <ActionButtonsGrid type={ACTION_BUTTON_TYPE.TERTIARY}>
        <LoadingButton
          color="primary"
          disabled={loadingSubmit}
          fullWidth
          id={authorizeButtonId}
          loading={loadingSubmit}
          onClick={handleSubmit}
          size="large"
          variant="contained"
        >
          Authorize
        </LoadingButton>
      </ActionButtonsGrid>
    </>
  );
}

IsThisCorrectFormACH.propTypes = {
  handleClose: PropTypes.func.isRequired,
  handleClosePreviousModal: PropTypes.func.isRequired
};
